import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Grid, 
  Typography, 
  Card, 
  CardContent, 
  CardHeader,
  CircularProgress,
  Divider,
  useTheme,
  TextField,
  InputAdornment,
  IconButton,
  Autocomplete,
  Avatar,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Button,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Chip,
  Snackbar,
  Checkbox,
  ListItemText,
  useMediaQuery,
  Fab,
  Zoom,
  CardActionArea,
  CardActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction
} from '@mui/material'; 
import { styled } from '@mui/material/styles';
import { 
  WaterDrop, 
  TrendingUp, 
  CalendarToday,
  LocationCity,
  Clear,
  LocationOn,
  Business,
  Search,
  ArrowForward,
  Add,
  Edit,
  Delete,
  FilterList,
  Upload,
  GetApp,
  Bolt,
  MyLocation
} from '@mui/icons-material';
import { Devices as DevicesIcon } from '@mui/icons-material';
import dashboardService from '../services/dashboardService';
import siteService from '../services/siteService';
import deviceService from '../services/deviceService';
import accountService from '../services/accountService';
import { useRefresh } from '../context/RefreshContext';
import MuiAlert from '@mui/material/Alert';
import { useAuth } from '../context/AuthContext';
import StructuredLocationFields from '../components/StructuredLocationFields';

// Alert component for Snackbar
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Sites = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { refreshCounter, refreshData } = useRefresh(); // Get refresh functionality from context
  const { user: authUser } = useAuth();
  const isAdmin = authUser?.role === 'admin';
  const isAdminOrOperator = isAdmin || authUser?.role === 'operator';
  const isEndUser = authUser?.role === 'end_user';
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [devices, setDevices] = useState([]);
  const [siteTypeCounts, setSiteTypeCounts] = useState({}); // { [siteId]: { water, electrical } }
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('description');
  const [sortDir, setSortDir] = useState('asc');
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('add'); // 'add', 'edit', or 'delete'
  const [currentSite, setCurrentSite] = useState(null);
  const [sitesUsageData, setSitesUsageData] = useState({});
  const [siteFormData, setSiteFormData] = useState({
    description: '',
    location: '',
    latitude: '',
    longitude: '',
    accountId: '',
    leakNightFlowThresholdLph: '0.5',
    leakNightContinuousHours: '4',
    leakNightStartHour: '0',
    leakNightEndHour: '24',
    signalStrengthThresholdReadings: '16'
  });
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [siteDevices, setSiteDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvPreview, setCsvPreview] = useState([]);
  const [gpsLocating, setGpsLocating] = useState(false);

  // Load sites data function - moved outside useEffect so it can be called from other functions
  const loadData = async () => {
    setLoading(true);
    try {
      // Load sites
      const sitesData = await siteService.fetchSites();
      setSites(sitesData);
      
      // Load all devices for device selection (up to 10,000) in lean mode to reduce payload
      let devicesData = await deviceService.fetchDevices({ limit: 10000, lean: true });
      let normalizedDevices = [];
      if (Array.isArray(devicesData)) {
        normalizedDevices = devicesData;
      } else if (devicesData && Array.isArray(devicesData.devices)) {
        normalizedDevices = devicesData.devices;
      }
      setDevices(normalizedDevices);

      // Build per-site type counts from devices.sites relations
      const counts = {};
      for (const d of normalizedDevices) {
        const siteRefs = Array.isArray(d?.sites) ? d.sites : [];
        for (const s of siteRefs) {
          const sid = (s?.id ?? s)?.toString?.() || String(s);
          if (!sid) continue;
          if (!counts[sid]) counts[sid] = { water: 0, electrical: 0 };
          if (d?.deviceType === 'electrical_meter') counts[sid].electrical += 1;
          else counts[sid].water += 1;
        }
      }
      // Fallback: for sites without counts from normalizedDevices, fetch per-site devices
      const missingSiteIds = (sitesData || [])
        .map(s => s.id?.toString?.() || s.id)
        .filter(id => !counts[id]);

      if (missingSiteIds.length > 0) {
        for (const sid of missingSiteIds) {
          try {
            const siteDevicesData = await siteService.fetchSiteDevices(sid);
            const water = (siteDevicesData || []).filter(d => d?.deviceType !== 'electrical_meter').length;
            const electrical = (siteDevicesData || []).filter(d => d?.deviceType === 'electrical_meter').length;
            counts[sid] = { water, electrical };
          } catch (e) {
            counts[sid] = { water: 0, electrical: 0 };
          }
        }
      }

      setSiteTypeCounts(counts);
      
      // Load accounts
      if (!isEndUser) {
        const accountsData = await accountService.fetchAccounts();
        setAccounts(Array.isArray(accountsData) ? accountsData : []);
      }
      setLoadingAccounts(false);
      
      // Load usage data for all sites (batched + skip empty sites)
      const usageData = {};
      const toSiteKey = (s) => s?.id?.toString?.() || String(s?.id);
      const siteWork = (sitesData || []).map((site) => {
        const siteKey = toSiteKey(site);
        const c = counts?.[siteKey] || { water: 0, electrical: 0 };
        const deviceCount = (c.water || 0) + (c.electrical || 0);

        // If no devices, skip expensive usage calls entirely.
        if (deviceCount === 0) {
          usageData[site.id] = { todayUsage: 0, yesterdayUsage: 0, last30DaysUsage: 0 };
          return null;
        }

        return async () => {
          try {
            const [todayUsage, yesterdayUsage, last30DaysUsage] = await Promise.all([
              dashboardService.fetchTodayUsage(site.id),
              dashboardService.fetchYesterdayUsage(site.id),
              dashboardService.fetchLast30DaysUsage(site.id)
            ]);

            usageData[site.id] = {
              todayUsage: todayUsage?.totalUsage || 0,
              yesterdayUsage: yesterdayUsage?.totalUsage || 0,
              last30DaysUsage: last30DaysUsage?.totalUsage || 0
            };
          } catch (error) {
            console.error(`Error loading usage data for site ${site.id}:`, error);
            usageData[site.id] = { todayUsage: 0, yesterdayUsage: 0, last30DaysUsage: 0 };
          }
        };
      }).filter(Boolean);

      const MAX_CONCURRENCY = 4;
      for (let i = 0; i < siteWork.length; i += MAX_CONCURRENCY) {
        const batch = siteWork.slice(i, i + MAX_CONCURRENCY);
        await Promise.all(batch.map((fn) => fn()));
      }

      setSitesUsageData(usageData);
    } catch (error) {
      console.error('Error loading data:', error);
      setSnackbar({
        open: true,
        message: `Error loading data: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Load sites data when the component mounts or when refresh is triggered
  useEffect(() => {
    loadData();
  }, [refreshCounter]);

  const handleSiteClick = (siteId) => {
    navigate(`/sites/${siteId}`);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value || '');
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleAddSite = () => {
    if (!isAdminOrOperator) return;
    setSiteFormData({
      description: '',
      location: '',
      latitude: '',
      longitude: '',
      accountId: '',
      leakNightFlowThresholdLph: '0.5',
      leakNightContinuousHours: '4',
      leakNightStartHour: '0',
      leakNightEndHour: '24',
      signalStrengthThresholdReadings: '16'
    });
    setDialogType('add');
    setOpenDialog(true);
  };

  const handleEditSite = async (site, event) => {
    event.stopPropagation();
    setCurrentSite(site);
    setSiteFormData({
      description: site.description || '',
      location: site.location || '',
      latitude: site.latitude !== undefined && site.latitude !== null ? String(site.latitude) : '',
      longitude: site.longitude !== undefined && site.longitude !== null ? String(site.longitude) : '',
      accountId: site.accountId !== undefined && site.accountId !== null ? String(site.accountId) : '',
      leakNightFlowThresholdLph: site.leakNightFlowThresholdLph !== undefined && site.leakNightFlowThresholdLph !== null ? String(site.leakNightFlowThresholdLph) : '0.5',
      leakNightContinuousHours: site.leakNightContinuousHours !== undefined && site.leakNightContinuousHours !== null ? String(site.leakNightContinuousHours) : '4',
      leakNightStartHour: site.leakNightStartHour !== undefined && site.leakNightStartHour !== null ? String(site.leakNightStartHour) : '0',
      leakNightEndHour: site.leakNightEndHour !== undefined && site.leakNightEndHour !== null ? String(site.leakNightEndHour) : '24',
      signalStrengthThresholdReadings: site.signalStrengthThresholdReadings !== undefined && site.signalStrengthThresholdReadings !== null ? String(site.signalStrengthThresholdReadings) : '16'
    });
    
    // Load devices linked to this site
    setLoadingDevices(true);
    try {
      const siteDevicesData = await siteService.fetchSiteDevices(site.id);
      setSiteDevices(siteDevicesData);

      // Extract device IDs for pre-selection and ensure all are strings
      const deviceIds = siteDevicesData.map(device => device.id.toString());
      setSelectedDevices(deviceIds);
    } catch (error) {
      console.error(`Error loading devices for site ${site.id}:`, error);
      setSiteDevices([]);
      setSelectedDevices([]);
    } finally {
      setLoadingDevices(false);
    }
    
    setDialogType('edit');
    setOpenDialog(true);
  };

  const handleDeleteSite = (site, event) => {
    event.stopPropagation();
    if (!isAdmin) return;
    setCurrentSite(site);
    setDialogType('delete');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSiteFormChange = (event) => {
    const { name, value } = event.target;
    setSiteFormData({
      ...siteFormData,
      [name]: value
    });
  };
  
  const handleDeviceSelectionChange = (event) => {
    const value = event.target.value;
    setSelectedDevices(value);
  };

  const handleUseCurrentGps = async () => {
    if (gpsLocating) return;

    try {
      if (!navigator.geolocation) {
        setSnackbar({ open: true, message: 'Geolocation is not supported by this browser', severity: 'error' });
        return;
      }

      setGpsLocating(true);

      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0
        });
      });

      const lat = position?.coords?.latitude;
      const lng = position?.coords?.longitude;

      if (typeof lat !== 'number' || typeof lng !== 'number') {
        setSnackbar({ open: true, message: 'Unable to read GPS coordinates', severity: 'error' });
        return;
      }

      setSiteFormData((prev) => ({
        ...prev,
        latitude: lat.toFixed(7),
        longitude: lng.toFixed(7)
      }));
    } catch (e) {
      const code = e?.code;
      let msg = e?.message || 'Failed to get current GPS location';
      if (code === 1) msg = 'Location permission denied';
      if (code === 2) msg = 'Location unavailable';
      if (code === 3) msg = 'Location request timed out';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setGpsLocating(false);
    }
  };

  const handleCsvImport = () => {
    if (!isAdminOrOperator) return;
    setCsvImportOpen(true);
  };

  const handleCloseCsvImport = () => {
    setCsvImportOpen(false);
    setCsvFile(null);
    setCsvPreview([]);
  };

  const handleCsvFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
      // Read and preview the CSV
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        const preview = lines.slice(0, 6); // Show first 5 rows + header
        setCsvPreview(preview);
      };
      reader.readAsText(file);
    } else {
      setSnackbar({
        open: true,
        message: 'Please select a valid CSV file',
        severity: 'error'
      });
    }
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      setSnackbar({
        open: true,
        message: 'Please select a CSV file first',
        severity: 'error'
      });
      return;
    }

    setCsvImporting(true);
    try {
      const formData = new FormData();
      formData.append('csvFile', csvFile);

      const response = await siteService.importSitesFromCsv(formData);

      const sitesImported = response?.sitesImported ?? response?.imported ?? 0;
      const devicesImported = response?.devicesImported ?? 0;
      const skipped = response?.skipped ?? 0;
      
      setSnackbar({
        open: true,
        message: `Successfully imported ${sitesImported} sites and ${devicesImported} devices (skipped ${skipped})`,
        severity: 'success'
      });
      
      // Refresh the sites list
      await loadData();
      handleCloseCsvImport();
    } catch (error) {
      console.error('CSV import error:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to import CSV file',
        severity: 'error'
      });
    } finally {
      setCsvImporting(false);
    }
  };

  const downloadCsvTemplate = () => {
    const csvContent = `type,description,location,latitude,longitude,siteType,deviceType,serialNumber,isActive,multiplier,parentSerialNumber,accountNumber
"site","Downtown Office Complex","123 Business Plaza, Main Street, Cape Town","-33.9248685","18.4240553","Commercial","","","","","",""
"device","Main Water Meter","Building Entrance","-33.9249000","18.4240000","","water_meter","WM001","true","1","","ACC001"
"device","Flow Sensor A","Pipe Section 1","-33.9249100","18.4240100","","flow_sensor","FS001","true","1000","WM001",""
"site","Riverside Apartments","456 River View Drive, Stellenbosch","-33.9349000","18.8608000","Residential","","","","","",""
"device","Secondary Water Meter","Basement","","","","water_meter","WM002","true","1","","ACC002"
"device","Temperature Sensor","Hot Water Line","","","","temperature_sensor","TS001","true","1","WM002",""`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sites_with_devices_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleSaveForm = async () => {
    try {
      setSubmitting(true);

      const apiData = {
        description: siteFormData.description,
        location: siteFormData.location,
        latitude: siteFormData.latitude === '' || siteFormData.latitude === null || siteFormData.latitude === undefined
          ? null
          : Number(siteFormData.latitude),
        longitude: siteFormData.longitude === '' || siteFormData.longitude === null || siteFormData.longitude === undefined
          ? null
          : Number(siteFormData.longitude),
        accountId: siteFormData.accountId === '' || siteFormData.accountId === null || siteFormData.accountId === undefined
          ? null
          : Number(siteFormData.accountId),
        leakNightFlowThresholdLph: siteFormData.leakNightFlowThresholdLph === '' || siteFormData.leakNightFlowThresholdLph === null || siteFormData.leakNightFlowThresholdLph === undefined
          ? null
          : Number(siteFormData.leakNightFlowThresholdLph),
        leakNightContinuousHours: siteFormData.leakNightContinuousHours === '' || siteFormData.leakNightContinuousHours === null || siteFormData.leakNightContinuousHours === undefined
          ? null
          : Number(siteFormData.leakNightContinuousHours),
        leakNightStartHour: siteFormData.leakNightStartHour === '' || siteFormData.leakNightStartHour === null || siteFormData.leakNightStartHour === undefined
          ? null
          : Number(siteFormData.leakNightStartHour),
        leakNightEndHour: siteFormData.leakNightEndHour === '' || siteFormData.leakNightEndHour === null || siteFormData.leakNightEndHour === undefined
          ? null
          : Number(siteFormData.leakNightEndHour),
        signalStrengthThresholdReadings: siteFormData.signalStrengthThresholdReadings === '' || siteFormData.signalStrengthThresholdReadings === null || siteFormData.signalStrengthThresholdReadings === undefined
          ? null
          : Number(siteFormData.signalStrengthThresholdReadings)
      };
      
      if (dialogType === 'add') {
        if (!isAdminOrOperator) {
          setSubmitting(false);
          return;
        }
        // Call the API to add a new site
        const newSite = await siteService.createSite(apiData);
        
        // Link selected devices to the new site if any were selected
        if (selectedDevices.length > 0) {
          await siteService.updateSiteDevices(newSite.id, selectedDevices);
        }
        
        setSites([...sites, newSite]);
        setSnackbar({
          open: true,
          message: 'Site added successfully',
          severity: 'success'
        });
      } else if (dialogType === 'edit') {
        if (!isAdminOrOperator) {
          delete apiData.accountId;
        }
        // Call the API to update the site
        const updatedSite = await siteService.updateSite(currentSite.id, apiData);
        
        // Update site-device relationships (admin and operator only)
        if (isAdminOrOperator) {
          await siteService.updateSiteDevices(currentSite.id, selectedDevices);
        }
        
        const updatedSites = sites.map(site => 
          site.id === currentSite.id ? { ...site, ...updatedSite } : site
        );
        setSites(updatedSites);
        setSnackbar({
          open: true,
          message: 'Site updated successfully',
          severity: 'success'
        });
        
        // Trigger refresh after edit operation
        refreshData();
      } else if (dialogType === 'delete') {
        if (!isAdmin) {
          setSubmitting(false);
          return;
        }
        // Call the API to delete the site
        await siteService.deleteSite(currentSite.id);
        const filteredSites = sites.filter(site => site.id !== currentSite.id);
        setSites(filteredSites);
        setSnackbar({
          open: true,
          message: 'Site deleted successfully',
          severity: 'success'
        });
        
        // Trigger refresh after delete operation
        refreshData();
      }
      setOpenDialog(false);
    } catch (error) {
      console.error('Error processing site operation:', error);
      setSnackbar({
        open: true,
        message: `Error: ${error.message || 'An unknown error occurred'}`,
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Filter sites based on search term for the Autocomplete component
  const getFilterOptions = (options, { inputValue }) => {
    const searchLower = inputValue.toLowerCase();
    return options.filter(site => 
      (site.description && site.description.toLowerCase().includes(searchLower)) ||
      (site.location && site.location.toLowerCase().includes(searchLower))
    );
  };

  // Format numbers with commas and fixed decimal places
  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    
    if (typeof num === 'string') {
      num = parseFloat(num);
    }
    
    if (isNaN(num)) return '0';
    
    // Format with commas and 2 decimal places
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Filter sites based on search
  const filteredSites = sites.filter(site => {
    const matchesSearch = 
      site.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      site.location.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  }).sort((a, b) => {
    const dirFactor = sortDir === 'desc' ? -1 : 1;
    if (sortBy === 'deviceCount') return ((a.deviceCount || 0) - (b.deviceCount || 0)) * dirFactor;
    const av = String(a?.[sortBy] || '');
    const bv = String(b?.[sortBy] || '');
    return av.localeCompare(bv, undefined, { sensitivity: 'base', numeric: true }) * dirFactor;
  });

  const requestSort = (key) => {
    if (sortBy === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const renderSortHeader = (key, label, align) => (
    <TableCell align={align} sortDirection={sortBy === key ? sortDir : false}>
      <TableSortLabel
        active={sortBy === key}
        direction={sortBy === key ? sortDir : 'asc'}
        onClick={() => requestSort(key)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [fabVisible, setFabVisible] = useState(true);

  // Hide FAB when scrolling down, show when scrolling up
  useEffect(() => {
    let lastScrollTop = 0;
    const handleScroll = () => {
      const st = window.pageYOffset || document.documentElement.scrollTop;
      if (st > lastScrollTop && st > 100) {
        // Scrolling down
        setFabVisible(false);
      } else {
        // Scrolling up or at top
        setFabVisible(true);
      }
      lastScrollTop = st <= 0 ? 0 : st;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: 'calc(100vh - 200px)' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  // Styled components for better mobile experience
  const StyledCard = styled(Card)(({ theme }) => ({
    marginBottom: theme.spacing(1.5),
    transition: 'box-shadow 0.3s ease-in-out',
    '&:hover': {
      boxShadow: theme.shadows[8],
    },
  }));

  const StatItem = ({ label, value, icon: Icon }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 1 }}>
      <Typography variant="caption" color="textSecondary">{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
        {Icon && <Icon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />}
        <Typography variant="body2" fontWeight="medium">{value}</Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 2 },
      pb: 0,
      maxWidth: '100%',
      mx: 'auto',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap',
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' }, 
        gap: 1,
        mb: 2
      }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" gutterBottom sx={{ mr: 1 }}>
          Sites
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flexBasis: { xs: '100%', sm: 'auto' }, mt: { xs: 0.5, sm: 0 }, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
          {isAdminOrOperator && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={handleAddSite}
              size={isMobile ? 'small' : 'medium'}
            >
              Add Site
            </Button>
          )}

          {isAdminOrOperator && (
            <Button
              variant="outlined"
              startIcon={<Upload />}
              onClick={handleCsvImport}
              size={isMobile ? 'small' : 'medium'}
            >
              Import CSV
            </Button>
          )}
        </Box>
      </Box>

      {/* Search */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          size={isMobile ? 'small' : 'medium'}
          placeholder="Search Sites..."
          value={searchTerm}
          onChange={handleSearchChange}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton onClick={handleClearSearch} edge="end" size={isMobile ? 'small' : 'medium'}>
                  <Clear />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Paper>

      {/* Sites List */}
      {isMobile ? (
        // Mobile Card View
        <Box sx={{ mb: 8 }}>
          {filteredSites.length > 0 ? (
            filteredSites.map((site) => (
              <StyledCard key={site.id} elevation={2}>
                <CardActionArea onClick={() => handleSiteClick(site.id)}>
                  <CardContent sx={{ p: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {/* Icons indicating device types present at the site */}
                      {(() => {
                        const c = siteTypeCounts[site.id?.toString?.() || site.id] || { water: 0, electrical: 0 };
                        const hasWater = c.water > 0;
                        const hasElec = c.electrical > 0;
                        if (!hasWater && !hasElec) {
                          return (
                            <WaterDrop 
                              fontSize="small" 
                              sx={{ mr: 1, color: theme.palette.primary.main, fontSize: '1.3rem' }}
                            />
                          );
                        }
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                            {hasWater && (
                              <WaterDrop fontSize="small" sx={{ mr: 0.75, color: theme.palette.primary.main, fontSize: '1.2rem' }} />
                            )}
                            {hasElec && (
                              <Bolt fontSize="small" sx={{ color: theme.palette.warning.main, fontSize: '1.2rem' }} />
                            )}
                          </Box>
                        );
                      })()}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" component="div" noWrap>
                          {site.description}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.25 }}>
                          <LocationOn fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                          <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {site.location}
                          </Typography>
                        </Box>
                      </Box>
                      <Chip 
                        icon={<DevicesIcon fontSize="small" />} 
                        label={site.deviceCount || 0}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ ml: 0.5 }}
                      />
                      <IconButton 
                        size="small" 
                        disabled={isEndUser}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isEndUser) return;
                          handleEditSite(site, e);
                        }}
                        color="primary"
                        sx={{ ml: 0.25 }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        disabled={!isAdmin}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isAdmin) return;
                          handleDeleteSite(site, e);
                        }}
                        color="error"
                        sx={{ ml: 0.25 }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </StyledCard>
            ))
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              p: 4,
              textAlign: 'center'
            }}>
              <Search fontSize="large" color="disabled" sx={{ mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No sites found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {searchTerm ? 'Try a different search term' : 'Add a new site to get started'}
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        // Desktop Table View
        <TableContainer component={Paper} elevation={2} sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
          <Table sx={{ minWidth: 650 }} aria-label="sites table">
            <TableHead>
              <TableRow>
                {renderSortHeader('description', 'Site')}
                {renderSortHeader('location', 'Location')}
                {renderSortHeader('deviceCount', 'Devices', 'center')}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSites.length > 0 ? (
                filteredSites.map((site) => (
                  <TableRow
                    key={site.id}
                    hover
                    sx={{ 
                      cursor: 'pointer',
                      '&:last-child td, &:last-child th': { border: 0 },
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                    onClick={() => handleSiteClick(site.id)}
                  >
                    <TableCell component="th" scope="row">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {(() => {
                          const c = siteTypeCounts[site.id?.toString?.() || site.id] || { water: 0, electrical: 0 };
                          const hasWater = c.water > 0;
                          const hasElec = c.electrical > 0;
                          if (!hasWater && !hasElec) {
                            return <WaterDrop sx={{ mr: 1.5, color: theme.palette.primary.main }} />;
                          }
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', mr: 1.5 }}>
                              {hasWater && (<WaterDrop sx={{ mr: 0.75, color: theme.palette.primary.main }} />)}
                              {hasElec && (<Bolt sx={{ color: theme.palette.warning.main }} />)}
                            </Box>
                          );
                        })()}
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          {site.description}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocationOn fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        {site.location}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        icon={<DevicesIcon fontSize="small" />} 
                        label={site.deviceCount || 0}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ 
                          minWidth: 60,
                          '& .MuiChip-icon': {
                            ml: 0.5,
                            mr: -0.5
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Tooltip title="Edit Site">
                          <span>
                            <IconButton
                              size="small"
                              disabled={isEndUser}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isEndUser) return;
                                handleEditSite(site, e);
                              }}
                              color="primary"
                              sx={{ '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' } }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete Site">
                          <span>
                            <IconButton
                              size="small"
                              disabled={!isAdmin}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isAdmin) return;
                                handleDeleteSite(site, e);
                              }}
                              color="error"
                              sx={{ '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' } }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Search fontSize="large" color="disabled" sx={{ mb: 1 }} />
                      <Typography variant="subtitle1" color="textSecondary">
                        No sites found
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {searchTerm ? 'Try a different search term' : 'Add a new site to get started'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Removed mobile FAB as per design update */}

      {/* Add/Edit Site Dialog */}
      <Dialog 
        open={openDialog && (dialogType === 'add' || dialogType === 'edit')}
        onClose={handleCloseDialog}
        fullScreen={isMobile}
        scroll="paper"
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: { xs: '100%', sm: 900 },
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: '100dvh', sm: 'calc(100vh - 64px)' },
            '& .MuiDialogActions-root': {
              p: isMobile ? 2 : 3,
              pt: 1,
            },
          }
        }}
      >
        <DialogTitle>{dialogType === 'add' ? 'Add New Site' : 'Edit Site'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="description"
            label="Site Name"
            type="text"
            fullWidth
            variant="outlined"
            value={siteFormData.description}
            onChange={handleSiteFormChange}
            required
          />
          <StructuredLocationFields
            value={siteFormData.location}
            onChange={(nextLocation) => setSiteFormData((p) => ({ ...p, location: nextLocation }))}
            required
          />
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <TextField
              margin="dense"
              name="latitude"
              label="Latitude (Optional)"
              type="number"
              inputProps={{ step: 0.0000001, min: -90, max: 90 }}
              fullWidth
              variant="outlined"
              value={siteFormData.latitude}
              onChange={handleSiteFormChange}
            />
            <TextField
              margin="dense"
              name="longitude"
              label="Longitude (Optional)"
              type="number"
              inputProps={{ step: 0.0000001, min: -180, max: 180 }}
              fullWidth
              variant="outlined"
              value={siteFormData.longitude}
              onChange={handleSiteFormChange}
            />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title="Use current GPS">
                <span>
                  <IconButton onClick={handleUseCurrentGps} disabled={gpsLocating}>
                    {gpsLocating ? <CircularProgress size={20} /> : <MyLocation />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
            <TextField
              margin="dense"
              label="Leak Night Flow Threshold (L/h)"
              name="leakNightFlowThresholdLph"
              type="number"
              inputProps={{ min: 0, step: 0.1 }}
              value={siteFormData.leakNightFlowThresholdLph}
              onChange={handleSiteFormChange}
              disabled={isEndUser}
              sx={{ flex: 1, minWidth: 220 }}
            />
            <TextField
              margin="dense"
              label="Leak Night Continuous Hours"
              name="leakNightContinuousHours"
              type="number"
              inputProps={{ min: 1, step: 1 }}
              value={siteFormData.leakNightContinuousHours}
              onChange={handleSiteFormChange}
              disabled={isEndUser}
              sx={{ flex: 1, minWidth: 220 }}
            />
            <FormControl margin="dense" sx={{ flex: 1, minWidth: 220 }} disabled={isEndUser}>
              <InputLabel id="leakNightStartHour-label">Leak Night Start</InputLabel>
              <Select
                labelId="leakNightStartHour-label"
                label="Leak Night Start"
                name="leakNightStartHour"
                value={siteFormData.leakNightStartHour}
                onChange={handleSiteFormChange}
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <MenuItem key={h} value={String(h)}>
                    {String(h).padStart(2, '0')}:00
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl margin="dense" sx={{ flex: 1, minWidth: 220 }} disabled={isEndUser}>
              <InputLabel id="leakNightEndHour-label">Leak Night End</InputLabel>
              <Select
                labelId="leakNightEndHour-label"
                label="Leak Night End"
                name="leakNightEndHour"
                value={siteFormData.leakNightEndHour}
                onChange={handleSiteFormChange}
              >
                {Array.from({ length: 24 }, (_, i) => i + 1).map((h) => (
                  <MenuItem key={h} value={String(h)}>
                    {String(h).padStart(2, '0')}:00
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              margin="dense"
              label="Signal Strength Threshold"
              name="signalStrengthThresholdReadings"
              type="number"
              inputProps={{ min: 1, step: 1 }}
              value={siteFormData.signalStrengthThresholdReadings}
              onChange={handleSiteFormChange}
              disabled={isEndUser}
              sx={{ flex: 1, minWidth: 220 }}
            />
          </Box>

          <FormControl fullWidth margin="dense">
            <InputLabel id="account-select-label">Account</InputLabel>
            <Select
              labelId="account-select-label"
              name="accountId"
              value={siteFormData.accountId || ''}
              label="Account"
              onChange={handleSiteFormChange}
              disabled={!isAdminOrOperator}
            >
              {loadingAccounts ? (
                <MenuItem value="">Loading accounts...</MenuItem>
              ) : (
                [
                  <MenuItem key="no-account" value="">
                    No Account
                  </MenuItem>,
                  ...accounts.map(account => (
                    <MenuItem key={account.id} value={String(account.id)}>
                      {account.name}
                    </MenuItem>
                  ))
                ]
              )}
            </Select>
          </FormControl>
          
          {/* Device Selection */}
          <Autocomplete
            multiple
            id="devices-autocomplete"
            options={devices}
            disabled={!isAdminOrOperator}
            loading={loadingDevices}
            disableCloseOnSelect
            limitTags={5}
            ChipProps={{ size: 'small' }}
            getOptionLabel={(option) => option.serialNumber || option.description || 'Unknown'}
            value={devices.filter(d => selectedDevices.includes(d.id.toString()))}
            onChange={(event, newValue) => {
              setSelectedDevices(newValue.map(d => d.id.toString()));
            }}
            isOptionEqualToValue={(option, value) => option.id.toString() === value.id.toString()}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Linked Devices"
                placeholder="Search devices..."
                margin="dense"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingDevices ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option.id}>
                <Checkbox
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                <ListItemText
                  primary={option.serialNumber || 'No Serial'}
                  secondary={option.description || 'No Description'}
                />
              </li>
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveForm} variant="contained" color="primary" disabled={submitting}>
            {dialogType === 'add' ? 'Add' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDialog && dialogType === 'delete'}
        onClose={handleCloseDialog}
        fullScreen={isMobile}
        scroll="paper"
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: { xs: '100%', sm: 450 },
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: '100dvh', sm: 'calc(100vh - 64px)' }
          }
        }}
      >
        <DialogTitle>Delete Site</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the site "{currentSite?.description}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveForm} variant="contained" color="error" disabled={submitting}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog
        open={csvImportOpen}
        onClose={handleCloseCsvImport}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        PaperProps={{
          sx: {
            maxHeight: { xs: '100dvh', sm: 'calc(100vh - 64px)' }
          }
        }}
      >
        <DialogTitle>
          Import Sites & Devices from CSV
          <Button
            startIcon={<GetApp />}
            onClick={downloadCsvTemplate}
            sx={{ float: 'right' }}
            size="small"
          >
            Download Template
          </Button>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Upload a CSV file to import sites and their linked devices in one operation. The CSV supports two formats:
            <br /><br />
            <strong>Enhanced Format (Sites + Devices):</strong> Use <strong>type</strong> column with "site" or "device" values.
            Device rows must follow their parent site. Columns: <strong>type, description, location, latitude, longitude, siteType, deviceType, serialNumber, isActive, multiplier, parentSerialNumber, accountNumber</strong>
            <br /><br />
            <strong>Legacy Format (Sites Only):</strong> Columns: <strong>description, location, latitude, longitude, type</strong>
          </DialogContentText>
          
          <Box sx={{ mb: 2 }}>
            <input
              accept=".csv"
              style={{ display: 'none' }}
              id="csv-file-input"
              type="file"
              onChange={handleCsvFileChange}
            />
            <label htmlFor="csv-file-input">
              <Button variant="outlined" component="span" startIcon={<Upload />}>
                Choose CSV File
              </Button>
            </label>
            {csvFile && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Selected: {csvFile.name}
              </Typography>
            )}
          </Box>

          {csvPreview.length > 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>Preview:</Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {csvPreview[0] && csvPreview[0].split(',').map((header, index) => (
                        <TableCell key={index}>
                          <strong>{header.replace(/"/g, '')}</strong>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {csvPreview.slice(1).map((row, index) => (
                      <TableRow key={index}>
                        {row.split(',').map((cell, cellIndex) => (
                          <TableCell key={cellIndex}>
                            {cell.replace(/"/g, '')}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCsvImport}>Cancel</Button>
          <Button 
            onClick={handleCsvUpload} 
            variant="contained" 
            disabled={!csvFile || csvImporting}
            startIcon={csvImporting ? <CircularProgress size={20} /> : <Upload />}
          >
            {csvImporting ? 'Importing...' : 'Import Sites'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Sites;
